import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { usePlanLimitStore } from '../store/planLimitStore';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── REQUEST INTERCEPTOR ──
api.interceptors.request.use((config) => {
  const { token, activeOrganization } = useAuthStore.getState();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (activeOrganization?.id) {
    config.headers["X-Organization-Id"] = activeOrganization.id;
  }

  return config;
});

// ── RESPONSE INTERCEPTOR (single, combined) ──
let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ── SUBSCRIPTION EXPIRED ──
    if (error.response?.data?.code === "SUBSCRIPTION_EXPIRED") {
      toast.error(error.response.data.error);
      window.location.href = "/billing?expired=1";
      return Promise.reject(error);
    }

    // ── PLAN LIMIT / SUBSCRIPTION REQUIRED ──
    if (
      error.response?.data?.code === "PLAN_LIMIT_REACHED" ||
      error.response?.data?.code === "NO_ACTIVE_SUBSCRIPTION" ||
      (error.response?.status === 403 &&
        (error.response?.data?.limitType ||
          error.response?.data?.limit !== undefined ||
          error.response?.data?.error?.toLowerCase().includes("upgrade your plan") ||
          error.response?.data?.error?.toLowerCase().includes("upgrade your subscription") ||
          error.response?.data?.error?.toLowerCase().includes("does not include")))
    ) {
      const errorData = error.response.data || {};
      usePlanLimitStore.getState().openLimitModal({
        title: errorData.limit === 0
          ? `${errorData.featureName || "Feature"} Not Included on Plan`
          : "Plan Quota Limit Reached",
        message: errorData.error,
        featureName: errorData.featureName,
        limitType: errorData.limitType,
        limit: errorData.limit,
        current: errorData.current,
        remaining: errorData.remaining,
      });
      return Promise.reject(error);
    }

    // ── 401 UNAUTHORIZED ──
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        useAuthStore.getState().logout();
        window.location.href = '/login?expired=1';
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for refresh to complete
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const { token: currentToken } = useAuthStore.getState();
        if (!currentToken) {
          throw new Error("No token available");
        }

        const refreshRes = await axios.post(
          `${import.meta.env.VITE_API_URL || '/api'}/v2/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );

        const { token: newToken } = refreshRes.data;

        // Update store with new token
        useAuthStore.setState({ token: newToken });

        // Update default header
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        onTokenRefreshed(newToken);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        useAuthStore.getState().logout();
        window.location.href = '/login?expired=1';
        return Promise.reject(refreshError);
      }
    }

    // ── 403 PLAN UPGRADE REQUIRED ──
    if (error.response?.status === 403 && error.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
      // Let component handle it — don't auto-redirect
    }

    // ── OTHER ERRORS ──
    const status = error.response?.status;
    const message = error.response?.data?.message || error.response?.data?.error || error.message;

    return Promise.reject({ status, message, data: error.response?.data });
  }
);

export default api;